import React, { useState, useEffect } from 'react';
import { Save, Loader2, CheckCircle } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const ConfigurationPage = ({ user }) => {
  const [config, setConfig] = useState({
    supplierNumber: '',
    shipToId: '',
    remitToCode: '',
    chartOfAccount: '',
    fullAccountCode: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      if (!user) return;
      
      try {
        const configRef = doc(db, 'userConfigs', user.id);
        const configDoc = await getDoc(configRef);
        
        if (configDoc.exists()) {
          setConfig(configDoc.data());
        }
      } catch (error) {
        console.error('Error loading config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      const configRef = doc(db, 'userConfigs', user.id);
      await setDoc(configRef, {
        ...config,
        updatedAt: new Date().toISOString()
      });
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Default Configuration</h2>
          <p className="text-gray-600">
            Set default values for your Coupa invoices. These can be applied to new invoices with one click.
          </p>
        </div>

        <div className="space-y-6">
          {/* Supplier Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supplier Number
            </label>
            <input
              type="text"
              value={config.supplierNumber}
              onChange={(e) => handleChange('supplierNumber', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter default supplier number"
            />
          </div>

          {/* Ship-To ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ship-To ID
            </label>
            <input
              type="text"
              value={config.shipToId}
              onChange={(e) => handleChange('shipToId', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter default ship-to ID"
            />
          </div>

          {/* Remit-To Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remit-To Code
            </label>
            <input
              type="text"
              value={config.remitToCode}
              onChange={(e) => handleChange('remitToCode', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter default remit-to code"
            />
          </div>

          {/* Chart of Account */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chart of Account
            </label>
            <input
              type="text"
              value={config.chartOfAccount}
              onChange={(e) => handleChange('chartOfAccount', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter default chart of account"
            />
          </div>

          {/* Full Account Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Account Code (20 Segments)
            </label>
            <input
              type="text"
              value={config.fullAccountCode}
              onChange={(e) => handleChange('fullAccountCode', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter full account code with 20 segments"
            />
            <p className="text-xs text-gray-500 mt-1">
              Example format: segment1-segment2-segment3-...
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 flex items-center gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Configuration
              </>
            )}
          </button>

          {saveSuccess && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Configuration saved successfully!</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPage;